import { FormikProps, useFormikContext } from 'formik'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import type { Installments } from '../../../../modules/privateOffer/reducer'
import { useStyles } from './InstallmentsInfo.styles'
import type { ChannelPartnerPrivateOfferInCreation } from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { ReactNode, useMemo } from 'react'
import { TableCell } from '@mui/material'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import {
  standardLabraDateFormat,
  numberFormat,
  isNonEmptyNumber,
} from '@labrav/common'
import { get, isNull, isUndefined } from 'lodash'
import { TableComponentV2, TextInputV2 } from '@labrav/react-components'

export const InstallmentsInfoTable = ({
  installments,
  editable = false,
}: {
  installments?: Installments[]
  editable?: boolean
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()

  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values, handleBlur, touched, errors, setFieldValue } = formikProps

  const installmentsList = editable ? values?.installments : installments

  const MetricMapping: Record<string, string> = {
    Payment: 'payment',
    'Payment to ISV ': 'isvAmount',
    'Payment from buyer': 'amount',
    'Invoice date': 'paymentDate',
  }

  const customHeaderMapping = useMemo(() => {
    let customHeader = {} as Record<string, ReactNode>
    Object.keys(MetricMapping).forEach((key, index) => {
      customHeader = {
        ...customHeader,
        [key]: (
          <TableCell
            className={clsx(
              classes.tableHead,
              index >= 1 ? classes.alignRight : ''
            )}
            key={MetricMapping[key]}
            data-testid={`header-${MetricMapping[key]}`}
          >
            {key}
          </TableCell>
        ),
      }
    })
    return customHeader
  }, [])

  const metricMappingDataObjects = (installmentsList || []).map(
    (installment: Installments, index: number) => {
      const dateValue = DateTime.fromISO(installment?.paymentDate, {
        zone: timeZone,
      }).toFormat(standardLabraDateFormat)

      const handleChangeMarkupPercentageValue = async (
        event: React.ChangeEvent<HTMLInputElement>
      ) => {
        const value = (event.target as HTMLInputElement).value
        await setFieldValue(
          `installments.[${index}].amount`,
          value === '' ? null : value
        )
        const updatedInstallments = values?.installments || []
        updatedInstallments[index].amount = Number(value)
        const totalAmount = updatedInstallments?.reduce(
          (acc, next) => acc + (Number(next?.amount) || 0),
          0
        )
        await setFieldValue('adjustmentPercentageInputValue', totalAmount)
      }

      const getErrorMsg = (keyName: string) => {
        return ((!!get(touched, keyName, false) && get(errors, keyName, ' ')) ||
          '') as string
      }

      return {
        payment: `${index + 1}`,
        isvAmount:
          !isNull(installment?.otherAmount) &&
          !isUndefined(installment?.otherAmount) ? (
            <div
              className={classes.price}
              data-testid={'installment-isv-amount-value'}
            >
              <span className={classes.currency}>$</span>{' '}
              {numberFormat(installment?.otherAmount)}
            </div>
          ) : (
            '-'
          ),
        amount: editable ? (
          <div className={classes.editableAmount}>
            <div className={classes.amountInput}>
              <TextInputV2
                keyName={`installments.[${index}].amount`}
                title={''}
                type="text"
                placeHolder=""
                value={
                  !isNull(installment?.amount) &&
                  isNonEmptyNumber(installment?.amount as number)
                    ? installment.amount!
                    : ''
                }
                updatedValue={
                  !isNull(installment?.amount) &&
                  isNonEmptyNumber(installment?.amount as number)
                    ? installment.amount!
                    : ''
                }
                isError={
                  !!(
                    get(touched, `installments.[${index}].amount`, false) &&
                    get(errors, `installments.[${index}].amount`, false)
                  )
                }
                onChange={handleChangeMarkupPercentageValue}
                isReadOnly={false}
                isRequired={false}
                errorText={getErrorMsg(`installments.[${index}].amount`)}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          </div>
        ) : !isNull(installment?.amount) &&
          !isUndefined(installment?.amount) ? (
          <div
            className={classes.price}
            data-testid={'installment-amount-value'}
          >
            <span className={classes.currency}>$</span>
            {numberFormat(installment?.amount)}
          </div>
        ) : (
          <div className={classes.alignRight}>-</div>
        ),

        paymentDate: (
          <div className={classes.alignRight} data-testid={'date-value'}>
            {dateValue}
          </div>
        ),
      }
    }
  )
  return (
    <div className={classes.root} data-testid={'fix-installments'}>
      <TableComponentV2
        objects={metricMappingDataObjects}
        mapping={MetricMapping}
        customHeader={customHeaderMapping}
        count={metricMappingDataObjects.length}
        fetchMoreData={() => void 0}
        onChangeCheckbox={() => void 0}
        selectedItem={[]}
        showCheckbox={false}
        isBasicTable={true}
        keyName="table-basic"
      />
    </div>
  )
}
